import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Loader2, Sparkles, Search, Globe, MapPin, BadgeCheck } from "lucide-react";

const splitTags = (s) => (s || "").split(",").map((t) => t.trim()).filter(Boolean);

export default function ClimateTechAcceleratorsPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.getClimateTechAccelerators(token).then((d) => setItems(d.items || [])).finally(() => setLoading(false));
  }, [token]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      (i.investor || "").toLowerCase().includes(q) ||
      (i.focus || "").toLowerCase().includes(q) ||
      (i.geography || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="space-y-6 animate-in" data-testid="climatetech-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Sparkles className="w-7 h-7" />ClimateTech Accelerators</h1>
          <p className="text-muted-foreground mt-1">Aceleradoras e inversores ClimateTech vigilados (solo lectura, sincronizado desde Airtable)</p>
        </div>
        <Badge variant="outline" data-testid="climatetech-count">{items.length} registros</Badge>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por inversor, foco o geografía..."
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="climatetech-search-input"
        />
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="climatetech-empty">Sin registros para mostrar.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="climatetech-grid">
          {filtered.map((i) => (
            <Card key={i.id} className="min-w-0" data-testid={`climatetech-card-${i.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold break-words">{i.investor}</p>
                </div>
                {i.stage && (
                  <div className="flex flex-wrap gap-1">
                    {splitTags(i.stage).map((s) => (
                      <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                    ))}
                  </div>
                )}
                {i.focus && (
                  <div className="flex flex-wrap gap-1">
                    {splitTags(i.focus).map((f) => (
                      <Badge key={f} variant="secondary" className="text-[10px] font-normal">{f}</Badge>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground pt-1">
                  {i.geography && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{i.geography}</span>}
                  {i.url && (
                    <a href={i.url} target="_blank" rel="noreferrer" className="text-blue-600 flex items-center gap-1 hover:underline">
                      <Globe className="w-3 h-3" />Web
                    </a>
                  )}
                </div>
                {i.financial_support && (
                  <p className="text-xs border-t pt-2 mt-2 flex items-center gap-1 text-muted-foreground">
                    {i.financial_support.toLowerCase() === "checked" ? (
                      <><BadgeCheck className="w-3.5 h-3.5 text-emerald-600" />Apoyo financiero verificado</>
                    ) : i.financial_support}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
